import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  DatePicker,
  List,
  Space,
  Spin,
  Alert,
  Tag,
  Button,
} from "antd";
import {
  BadgeDollarSign,
  ClipboardList,
  Users,
  Tag as TagIcon,
  ChevronRight,
  ShoppingBag,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import ReactApexChart from "react-apexcharts";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Dashboard = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      total_sales: 0,
      total_orders: 0,
      total_customers: 0,
      today_sales: 0,
      today_orders: 0,
      today_customers: 0,
      sales_percentage: 0,
      orders_percentage: 0,
      customers_percentage: 0,
    },
    latestOrders: [],
    storeStats: {
      total_sales: 0,
      average_order_sale: 0,
      sales_percentage: 0,
      avg_order_percentage: 0,
      total_users: 0,
      all_orders: 0,
    },
    visitors: {
      total_unique: 0,
      visitors_by_date: [],
      start_date: "",
      end_date: "",
    },
    salesChart: {
      period: "daily",
      labels: [],
      sales: [],
      orders: [],
    },
  });
  // console.log(dashboardData);

  useEffect(() => {
    // Get user data from localStorage
    const getUserFromLocalStorage = () => {
      try {
        const userData = localStorage.getItem("user");
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        }
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
      }
    };

    getUserFromLocalStorage();
    fetchCompleteDashboard();
  }, []);

  const fetchCompleteDashboard = (startDate = null, endDate = null) => {
    setLoading(true);

    // Build URL with query parameters
    let url = apiPath.dashboardComplete;
    const params = new URLSearchParams();

    if (startDate && endDate) {
      params.append("start_date", startDate);
      params.append("end_date", endDate);
      url = `${apiPath.dashboardComplete}?${params.toString()}`;
    }

    request({
      url: url,
      method: "GET",
      onSuccess: (data) => {
        // console.log(JSON.stringify(data));
        // console.log(JSON.stringify(data));
        console.log("all_orders:", data.storeStats?.all_orders);
        console.log("total_users:", data.storeStats?.total_users);
        console.log(data.storeStats);
        setDashboardData(data);
        setLoading(false);
      },
      onError: (err) => {
        console.error("Dashboard fetch error:", err);
        setErrorMessage(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load dashboard data.",
        );
        setLoading(false);
      },
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates, dateStrings) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      // Format dates as YYYY-MM-DD for API
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      fetchCompleteDashboard(startDate, endDate);
    } else {
      // Clear filter - fetch all data
      setDateRange(null);
      fetchCompleteDashboard();
    }
  };

  // Handle clear date filter
  const handleClearDateFilter = () => {
    setDateRange(null);
    fetchCompleteDashboard();
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "#10b981";
      case "pending":
        return "#f59e0b";
      case "cancelled":
        return "#ef4444";
      case "processing":
        return "#3b82f6";
      case "refunded":
        return "#8b5cf6";
      default:
        return "#94a3b8";
    }
  };

  // Get status text
  const getStatusText = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown";
  };

  // Handle order click
  const handleOrderClick = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const overallDetails = [
    {
      title: "Total Sales",
      value: formatCurrency(dashboardData.overview.total_sales),
      icon: <BadgeDollarSign size={24} color="#64748b" />,
      change: dashboardData.overview.sales_percentage,
      trend: dashboardData.overview.sales_percentage >= 0 ? "up" : "down",
      url: "/sales/orders",
    },
    {
      title: "Total Orders",
      value: formatNumber(dashboardData.storeStats.all_orders || 0),
      icon: <ClipboardList size={24} color="#64748b" />,
      change: dashboardData.overview.orders_percentage,
      trend: dashboardData.overview.orders_percentage >= 0 ? "up" : "down",
      url: "/sales/orders",
    },
    {
      title: "Total Customers",
      value: formatNumber(dashboardData.storeStats.total_users || 0),
      icon: <Users size={24} color="#64748b" />,
      change: dashboardData.overview.customers_percentage,
      trend: dashboardData.overview.customers_percentage >= 0 ? "up" : "down",
      url: "/customers",
    },
    {
      title: "Average Order Sale",
      value: formatCurrency(dashboardData.storeStats.average_order_sale),
      icon: <TagIcon size={24} color="#64748b" />,
      change: dashboardData.storeStats.avg_order_percentage,
      trend: dashboardData.storeStats.avg_order_percentage >= 0 ? "up" : "down",
      url: "/sales/orders",
    },
  ];

  const todaysDetails = [
    {
      title: "Today's Sales",
      value: formatCurrency(dashboardData.overview.today_sales),
      icon: <BadgeDollarSign size={24} color="#64748b" />,
    },
    {
      title: "Today's Orders",
      value: dashboardData.overview.today_orders,
      icon: <ClipboardList size={24} color="#64748b" />,
    },
    {
      title: "Today's Customers",
      value: dashboardData.overview.today_customers || 0,
      icon: <Users size={24} color="#64748b" />,
    },
  ];

  // Sales chart options
  const salesChartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
    },
    stroke: { curve: "smooth", width: 3 },
    colors: ["#3b82f6"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.3,
        stops: [0, 90, 100],
      },
    },
    xaxis: {
      categories: dashboardData.salesChart.labels,
      labels: {
        style: { colors: "#94a3b8", fontSize: "10px", fontWeight: 500 },
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#94a3b8", fontSize: "10px" },
        formatter: (value) => `${value.toFixed(0)}`,
      },
      show: true,
    },
    tooltip: {
      y: {
        formatter: (value) => formatCurrency(value),
      },
      theme: "dark",
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      position: "back",
    },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  const salesChartSeries = [
    {
      name: "Sales",
      data: dashboardData.salesChart.sales,
    },
  ];

  // Visitors chart options
  const visitorsChartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      background: "transparent",
    },
    plotOptions: {
      bar: {
        columnWidth: "35%",
        borderRadius: 6,
        borderRadiusApplication: "end",
        colors: {
          ranges: [
            {
              from: 0,
              to: 1000,
              color: "#ef4444",
            },
          ],
        },
      },
    },
    colors: ["#ef4444"],
    xaxis: {
      categories:
        dashboardData.visitors.visitors_by_date?.map((item) => item.date) || [],
      labels: {
        style: { colors: "#94a3b8", fontSize: "10px", fontWeight: 500 },
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#94a3b8", fontSize: "10px" },
      },
      show: true,
    },
    tooltip: {
      y: {
        formatter: (value) => `${value} visitor${value !== 1 ? "s" : ""}`,
      },
      theme: "dark",
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      position: "back",
    },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  const visitorsChartSeries = [
    {
      name: "Visitors",
      data:
        dashboardData.visitors.visitors_by_date?.map((item) => item.count) ||
        [],
    },
  ];

  const StatCard = ({ item }) => (
    <div
      className="stat-card-wrapper"
      onClick={() => navigate(item.url)}
      style={{ cursor: "pointer" }}
    >
      <div className="stat-card-body">
        <div className="stat-card-icon">{item.icon}</div>
        <div style={{ flex: 1 }}>
          <Text className="stat-card-value">{item.value}</Text>
          <Text className="stat-card-title">{item.title}</Text>
          {item.change !== undefined && (
            <div
              className={`stat-card-change ${item.trend === "up" ? "trend-up" : "trend-down"}`}
            >
              {item.trend === "up" ? (
                <TrendingUp size={12} />
              ) : (
                <TrendingDown size={12} />
              )}
              <span>{Math.abs(item.change)}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div
      className="dashboard-container"
      style={{ padding: "24px", background: "#f8fafc", minHeight: "100vh" }}
    >
      <div
        className="dashboard-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <Title
            level={4}
            className="dashboard-title"
            style={{
              margin: 0,
              color: "#0f172a",
              fontSize: "24px",
              fontWeight: 700,
            }}
          >
            Hi! {user?.name || "Admin"}
          </Title>
          <Text
            className="dashboard-subtitle"
            style={{
              color: "#64748b",
              fontSize: "14px",
              marginTop: "4px",
              display: "block",
            }}
          >
            Quickly Review what's going on in your store
          </Text>
        </div>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            style={{ borderRadius: "8px", borderColor: "#e2e8f0" }}
            placeholder={["Start Date", "End Date"]}
            format="YYYY-MM-DD"
            allowClear={true}
          />
          {dateRange && (
            <Button onClick={handleClearDateFilter} size="middle">
              Clear Filter
            </Button>
          )}
        </Space>
      </div>

      {errorMessage && (
        <Alert
          type="error"
          message="Dashboard load error"
          description={errorMessage}
          showIcon
          style={{ marginBottom: 24, borderRadius: "8px" }}
          closable
          onClose={() => setErrorMessage("")}
        />
      )}

      {/* Date Range Indicator */}
      {dateRange && (
        <Alert
          type="info"
          message={`Showing data from ${dateRange[0].format("YYYY-MM-DD")} to ${dateRange[1].format("YYYY-MM-DD")}`}
          showIcon
          style={{ marginBottom: 24, borderRadius: "8px" }}
          closable
          onClose={handleClearDateFilter}
        />
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          {/* Overall Details Section */}
          <div style={{ marginBottom: "32px" }}>
            <Text
              className="dashboard-section-title"
              style={{
                fontSize: "18px",
                fontWeight: 600,
                marginBottom: "20px",
                display: "block",
                color: "#0f172a",
              }}
            >
              Overall Details
            </Text>
            <Row gutter={[16, 16]}>
              {overallDetails.map((item, index) => (
                <Col xs={24} sm={12} key={index}>
                  <StatCard item={item} />
                </Col>
              ))}
            </Row>
          </div>

          {/* Today's Details Section */}
          <div style={{ marginBottom: "32px" }}>
            <Text
              className="dashboard-section-title"
              style={{
                fontSize: "18px",
                fontWeight: 600,
                marginBottom: "20px",
                display: "block",
                color: "#0f172a",
              }}
            >
              Today's Details
            </Text>
            <Row gutter={[16, 16]}>
              {todaysDetails.map((item, index) => (
                <Col xs={24} sm={8} key={index}>
                  <StatCard item={item} />
                </Col>
              ))}
            </Row>
          </div>

          {/* Latest Orders Section */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <Text
                className="dashboard-section-title"
                style={{ fontSize: "18px", fontWeight: 600, color: "#0f172a" }}
              >
                Latest Orders
              </Text>
              {dashboardData.latestOrders &&
                dashboardData.latestOrders.length > 0 && (
                  <Button
                    type="link"
                    onClick={() => navigate("/sales/orders")}
                    style={{ color: "#3b82f6", fontWeight: 500 }}
                  >
                    View All Orders{" "}
                    <ChevronRight size={14} style={{ marginLeft: "4px" }} />
                  </Button>
                )}
            </div>
            <Card
              bordered={false}
              className="latest-orders-card"
              bodyStyle={{ padding: 0 }}
              style={{
                borderRadius: "16px",
                boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
              }}
            >
              <List
                itemLayout="horizontal"
                dataSource={dashboardData.latestOrders || []}
                renderItem={(order, index) => (
                  <List.Item
                    className="order-list-item"
                    // onClick={() => handleOrderClick(order.id)}
                    style={{
                      padding: "20px 24px",
                      cursor: "pointer",
                      borderBottom:
                        index !== dashboardData.latestOrders?.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                      transition: "background 0.3s",
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <div
                          style={{
                            width: "52px",
                            height: "52px",
                            background: "#f1f5f9",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ShoppingBag size={24} color="#64748b" />
                        </div>
                      }
                      title={
                        <div>
                          <Text
                            strong
                            style={{
                              color: "#0f172a",
                              fontSize: "15px",
                              fontWeight: 600,
                            }}
                          >
                            {order.order_number}
                          </Text>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#64748b",
                              marginTop: "4px",
                            }}
                          >
                            {order.customer_name}
                          </div>
                        </div>
                      }
                      description={
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#94a3b8",
                            marginTop: "6px",
                          }}
                        >
                          {formatDate(order.created_at)}
                        </div>
                      }
                    />
                    <div style={{ textAlign: "right" }}>
                      <div
                        className="order-list-price"
                        style={{
                          fontWeight: 700,
                          color: "#0f172a",
                          fontSize: "18px",
                          marginBottom: "8px",
                        }}
                      >
                        {formatCurrency(order.total_amount)}
                      </div>
                      <div style={{ marginBottom: "6px" }}>
                        <Tag
                          style={{
                            borderRadius: "20px",
                            border: "none",
                            fontWeight: 500,
                            padding: "4px 12px",
                            background: getStatusColor(order.status),
                            color: "#fff",
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {getStatusText(order.status)}
                        </Tag>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: 500,
                        }}
                      >
                        {order.items_count} item
                        {order.items_count !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
              {(!dashboardData.latestOrders ||
                dashboardData.latestOrders.length === 0) && (
                <div style={{ padding: "60px 40px", textAlign: "center" }}>
                  <ShoppingBag
                    size={48}
                    color="#cbd5e1"
                    style={{ marginBottom: "16px" }}
                  />
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: "14px" }}
                  >
                    No orders found
                  </Text>
                  <Text
                    type="secondary"
                    style={{
                      display: "block",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    When customers place orders, they will appear here
                  </Text>
                </div>
              )}
            </Card>
          </div>
        </Col>

        <Col xs={24} lg={8}>
          {/* Store Stats Section */}
          <div style={{ marginBottom: "24px" }}>
            <Text
              className="dashboard-section-title"
              style={{
                fontSize: "18px",
                fontWeight: 600,
                marginBottom: "20px",
                display: "block",
                color: "#0f172a",
              }}
            >
              Store Stats
            </Text>
            <Card
              bordered={false}
              className="store-stats-card"
              bodyStyle={{ padding: "20px" }}
              style={{
                borderRadius: "16px",
                boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                className="store-stats-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <Text
                    className="store-stats-title"
                    style={{
                      color: "#64748b",
                      fontSize: "13px",
                      fontWeight: 500,
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Total Sales
                  </Text>
                  <Text
                    className="store-stats-value"
                    style={{
                      fontSize: "28px",
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    {formatCurrency(dashboardData.storeStats.total_sales)}
                  </Text>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Text
                    className="store-stats-date"
                    style={{
                      color: "#64748b",
                      fontSize: "11px",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    {dashboardData.visitors.start_date || "-"} -{" "}
                    {dashboardData.visitors.end_date || "-"}
                  </Text>
                  <Text
                    className="store-stats-subtext"
                    style={{
                      color: "#94a3b8",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {dashboardData.overview.total_orders} Orders
                  </Text>
                </div>
              </div>
              <ReactApexChart
                options={salesChartOptions}
                series={salesChartSeries}
                type="area"
                height={200}
              />

              <div style={{ marginTop: "28px" }}>
                <div
                  className="store-stats-header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <div>
                    <Text
                      className="store-stats-title"
                      style={{
                        color: "#64748b",
                        fontSize: "13px",
                        fontWeight: 500,
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      Visitors
                    </Text>
                    <Text
                      className="store-stats-value"
                      style={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {dashboardData.visitors.total_unique || 0}
                    </Text>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <Text
                      className="store-stats-date"
                      style={{
                        color: "#64748b",
                        fontSize: "11px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      {dashboardData.visitors.start_date || "-"} -{" "}
                      {dashboardData.visitors.end_date || "-"}
                    </Text>
                    <Text
                      className="store-stats-subtext"
                      style={{
                        color: "#94a3b8",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {dashboardData.visitors.total_unique || 0} unique
                    </Text>
                  </div>
                </div>
                <ReactApexChart
                  options={visitorsChartOptions}
                  series={visitorsChartSeries}
                  type="bar"
                  height={200}
                />
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      <style jsx>{`
        .stat-card-wrapper {
          background: white;
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          height: 100%;
        }
        .stat-card-wrapper:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          border-color: #cbd5e1;
        }
        .stat-card-body {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }
        .stat-card-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .stat-card-value {
          font-size: 28px;
          font-weight: 700;
          color: #0f172a;
          display: block;
          line-height: 1.2;
          margin-bottom: 6px;
        }
        .stat-card-title {
          font-size: 14px;
          color: #64748b;
          display: block;
          font-weight: 500;
        }
        .stat-card-change {
          font-size: 12px;
          margin-top: 8px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          border-radius: 20px;
          font-weight: 600;
        }
        .stat-card-change.trend-up {
          color: #10b981;
          background: #d1fae5;
        }
        .stat-card-change.trend-down {
          color: #ef4444;
          background: #fee2e2;
        }
        .order-list-item {
          transition: background 0.3s ease;
        }
        .order-list-item:hover {
          background: #f8fafc;
        }
        .latest-orders-card .ant-list-item {
          padding: 0;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
